/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
// ScoreManagementDialog.jsx - Dialog for managing KPI scores with matrix view

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    CircularProgress,
    Paper,
    Alert,
    Chip,
    Stack,
    alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import AddEditGoalDialog from './AddEditGoalDialog';

const ScoreManagementDialog = () => {
    const dispatch = useDispatch();
    const open = useSelector(state => state.userKpi.dialogs.scoreDialog);
    const userKpi = useSelector(state => state.userKpi.dialogData.userKpi);
    const userKpiWithGoals = useSelector(selectors.selectUserKpiWithGoals);
    const scores = useSelector(state => state.userKpi.userKpiScores) || [];
    const dialogLoading = useSelector(selectors.selectDialogLoading);
    const allUsers = useSelector(selectors.selectAllUsers) || [];

    const employeeUser = userKpi ? allUsers.find(u => 
        (u.user?.email || u.employeeId) === userKpi.userId
    ) : null;
    const employeeName = employeeUser 
        ? [employeeUser.firstname, employeeUser.middlename, employeeUser.lastname].filter(Boolean).join(' ')
        : userKpi?.userId;
    const employeeDesignation = userKpi?.designationAtAssignment?.name || employeeUser?.designation?.name || null;

    const [scoreDate, setScoreDate] = useState(dayjs());
    const [addingNewScore, setAddingNewScore] = useState(false);
    const [newScores, setNewScores] = useState({});
    const [remarksDialog, setRemarksDialog] = useState({ open: false, remark: '' });
    const [prevLoading, setPrevLoading] = useState(false);
    const [hoveredCell, setHoveredCell] = useState(null); // { goalId, date }
    const [editDialog, setEditDialog] = useState({ open: false, score: null, goalId: null, date: null, submitted: false });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, scoreId: null });
    const [addScoreDialog, setAddScoreDialog] = useState({ open: false, goalId: null, date: null, submitted: false });
    const [hasChanges, setHasChanges] = useState(false);
    const [addGoalDialogOpen, setAddGoalDialogOpen] = useState(false);
    const [remarksErrors, setRemarksErrors] = useState({});
    const dataFetchedRef = useRef(false);
    const prevOpenRef = useRef(false);

    const isScoreRemarksRequired = (score) => {
        const numScore = Number(score);
        return [0, 1, 5].includes(numScore);
    };

    const getRemarksError = (score, remarks) => {
        if (!isScoreRemarksRequired(score)) return '';
        const remarkText = (remarks || '').trim();
        if (remarkText.length === 0) return 'Remarks is required for this score value';
        if (remarkText.length < 50) return `Remarks must be at least 50 characters (${remarkText.length}/50)`;
        return '';
    };

    // Get period dates for date picker constraints (memoized to prevent infinite re-renders)
    const periodStartDate = useMemo(() => 
        userKpiWithGoals?.period?.startDate ? dayjs(userKpiWithGoals.period.startDate) : null,
        [userKpiWithGoals?.period?.startDate]
    );
    const periodEndDate = useMemo(() => 
        userKpiWithGoals?.period?.endDate ? dayjs(userKpiWithGoals.period.endDate) : null,
        [userKpiWithGoals?.period?.endDate]
    );
    const currentDate = useMemo(() => dayjs(), []);
    
    // Max date should be the earlier of current date or period end date
    const maxAllowedDate = useMemo(() => {
        if (!periodEndDate) return currentDate;
        return periodEndDate.isBefore(currentDate) ? periodEndDate : currentDate;
    }, [periodEndDate, currentDate]);

    // Fetch user KPI with goals and scores when dialog opens (only once per opening)
    useEffect(() => {
        // Detect when dialog just opened
        const justOpened = open && !prevOpenRef.current;
        
        if (justOpened && userKpi) {
            dispatch(actions.fetchUserKpiWithGoalsRequest(userKpi.userId, userKpi.periodId));
            dispatch(actions.fetchUserKpiScoresRequest(userKpi.id));
            dataFetchedRef.current = true;
        }
        
        // Reset when dialog closes
        if (!open && prevOpenRef.current) {
            dataFetchedRef.current = false;
            setHasChanges(false);
        }
        
        prevOpenRef.current = open;
    }, [open, userKpi, dispatch]);

    // Reset form after successful save
    useEffect(() => {
        if (prevLoading && !dialogLoading && addingNewScore) {
            // Save completed successfully
            setAddingNewScore(false);
            setNewScores({});
            // Reset to a valid date (prefer current date if within period, otherwise period start)
            const defaultDate = currentDate.isAfter(periodStartDate) && currentDate.isBefore(maxAllowedDate) 
                ? currentDate 
                : (periodStartDate || currentDate);
            setScoreDate(defaultDate);
        }
        setPrevLoading(dialogLoading);
    }, [dialogLoading, prevLoading, addingNewScore, periodStartDate, maxAllowedDate, currentDate]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setAddingNewScore(false);
            setNewScores({});
            // Reset to a valid date when dialog closes
            const defaultDate = currentDate.isAfter(periodStartDate) && currentDate.isBefore(maxAllowedDate) 
                ? currentDate 
                : (periodStartDate || currentDate);
            setScoreDate(defaultDate);
        }
    }, [open, periodStartDate, maxAllowedDate, currentDate]);

    const handleClose = () => {
        dispatch(actions.closeScoreDialog());
        // Only refetch the table if changes were made
        if (hasChanges) {
            dispatch(actions.fetchUserKpisRequest());
        }
    };

    const handleAddScoreClick = () => {
        setAddingNewScore(true);
        // Initialize newScores with empty values for each goal
        const initialScores = {};
        (userKpiWithGoals?.goals || []).forEach(goal => {
            initialScores[goal.id] = { score: '', remarks: '' };
        });
        setNewScores(initialScores);
        
        // Set initial date to current date if valid, otherwise period start date
        const periodStart = userKpiWithGoals?.period?.startDate ? dayjs(userKpiWithGoals.period.startDate) : null;
        const periodEnd = userKpiWithGoals?.period?.endDate ? dayjs(userKpiWithGoals.period.endDate) : null;
        const today = dayjs();
        const maxDate = periodEnd && periodEnd.isBefore(today) ? periodEnd : today;
        
        let initialDate = today;
        if (periodStart && today.isBefore(periodStart)) {
            initialDate = periodStart;
        } else if (maxDate && today.isAfter(maxDate)) {
            initialDate = maxDate;
        }
        
        setScoreDate(initialDate);
    };

    const handleCancelAdd = () => {
        setAddingNewScore(false);
        setNewScores({});
    };

    const handleScoreChange = (goalId, value) => {
        const numValue = value === '' ? '' : Number(value);
        if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 5)) {
            return; // Validate score between 0-5
        }
        setNewScores(prev => ({
            ...prev,
            [goalId]: { ...prev[goalId], score: value }
        }));
        // Clear remarks error when score changes
        setRemarksErrors(prev => ({ ...prev, [goalId]: '' }));
    };

    const handleScoreBlur = (goalId) => {
        setNewScores(prev => {
            const current = prev[goalId];
            if (!current || current.score === '' || current.score === null) return prev;

            let numValue = Number(current.score);
            if (isNaN(numValue)) return prev;

            // Round to the nearest integer
            numValue = Math.round(numValue);

            // Clamp between 0 and 5
            numValue = Math.max(0, Math.min(5, numValue));

            return {
                ...prev,
                [goalId]: { ...current, score: String(numValue) }
            };
        });
    };

    const handleRemarksChange = (goalId, value) => {
        setNewScores(prev => ({
            ...prev,
            [goalId]: { ...prev[goalId], remarks: value }
        }));
        // Clear remarks error when remarks change
        setRemarksErrors(prev => ({ ...prev, [goalId]: '' }));
    };

    const handleSaveScores = () => {
        // Validate remarks for scores 0, 1, 5
        const newErrors = {};
        let hasError = false;
        Object.keys(newScores).forEach(goalId => {
            const { score, remarks } = newScores[goalId];
            if (score !== '' && score !== null) {
                const error = getRemarksError(score, remarks);
                if (error) {
                    newErrors[goalId] = error;
                    hasError = true;
                }
            }
        });
        if (hasError) {
            setRemarksErrors(newErrors);
            return;
        }
        setRemarksErrors({});
        // Prepare scores array for bulk create
        const scoresToSave = [];
        
        Object.keys(newScores).forEach(goalId => {
            const { score, remarks } = newScores[goalId];
            if (score !== '' && score !== null) {
                scoresToSave.push({
                    userKpiGoalId: goalId,
                    score: Number(score),
                    remarks: remarks || null,
                    scoreDate: scoreDate.format('YYYY-MM-DD')
                });
            }
        });

        if (scoresToSave.length === 0) {
            return; // No scores to save
        }

        dispatch(actions.createBulkScoresRequest(scoresToSave));
        setHasChanges(true);
        
        // Hide the form after saving (like cancel button does)
        setAddingNewScore(false);
        setNewScores({});
    };

    const handleAddGoal = () => {
        setAddGoalDialogOpen(true);
    };

    const handleAddGoalDialogClose = () => {
        setAddGoalDialogOpen(false);
    };

    const handleGoalAdded = (goalData) => {
        if (!userKpiWithGoals) return;

        // Add new goal
        dispatch(actions.addGoalToUserKpiRequest(
            userKpiWithGoals.id,
            goalData
        ));

        // Don't set hasChanges - goal addition doesn't require main table reload
        // The goal is added optimistically to Redux state and will show in this dialog
        setAddGoalDialogOpen(false);
    };

    const handleShowRemark = (remark) => {
        setRemarksDialog({ open: true, remark });
    };

    const handleEditScore = (score, goalId, date) => {
        setEditDialog({ 
            open: true, 
            score: score,
            goalId,
            date,
            submitted: false
        });
    };

    const handleUpdateScore = () => {
        const { score, goalId, date } = editDialog;
        if (!score || !score.id) return;

        // Validate remarks for scores 0, 1, 5
        const remarksError = getRemarksError(score.score, score.remarks);
        if (remarksError) {
            setEditDialog(prev => ({ ...prev, submitted: true }));
            return;
        }

        const updateData = {
            score: score.score,
            remarks: score.remarks || null
        };

        dispatch(actions.updateScoreRequest(score.id, updateData));
        setHasChanges(true);
        setEditDialog({ open: false, score: null, goalId: null, date: null, submitted: false });
    };

    const handleDeleteClick = (scoreId) => {
        setDeleteDialog({ open: true, scoreId });
    };

    const handleConfirmDelete = () => {
        if (deleteDialog.scoreId) {
            dispatch(actions.deleteScoreRequest(deleteDialog.scoreId));
            setHasChanges(true);
            setDeleteDialog({ open: false, scoreId: null });
        }
    };

    const handleAddSingleScore = (goalId, date) => {
        setAddScoreDialog({ 
            open: true, 
            goalId,
            date,
            score: '',
            remarks: '',
            submitted: false
        });
    };

    const handleSaveNewScore = () => {
        const { goalId, date, score, remarks } = addScoreDialog;
        if (!goalId || !date || score === '' || score === null) return;

        // Validate remarks for scores 0, 1, 5
        const remarksError = getRemarksError(score, remarks);
        if (remarksError) {
            setAddScoreDialog(prev => ({ ...prev, submitted: true }));
            return;
        }

        const scoreData = {
            userKpiGoalId: goalId,
            score: Number(score),
            remarks: remarks || null,
            scoreDate: date
        };

        dispatch(actions.createScoreRequest(scoreData));
        setHasChanges(true);
        setAddScoreDialog({ open: false, goalId: null, date: null, score: '', remarks: '', submitted: false });
    };

    // Group scores by userKpiGoalId and scoreDate
    const groupedScores = {};
    scores.forEach(score => {
        const key = score.userKpiGoalId;
        if (!groupedScores[key]) {
            groupedScores[key] = [];
        }
        groupedScores[key].push(score);
    });

    // Get all unique score dates sorted (most recent first)
    const allScoreDates = [...new Set(scores.map(s => s.scoreDate))]
        .sort((a, b) => new Date(b) - new Date(a));

    const goals = userKpiWithGoals?.goals || [];

    // Check if a date already has scores (disable it to prevent multiple scoring on same day)
    const shouldDisableDate = (date) => {
        if (!date) return false;
        const dateString = date.format('YYYY-MM-DD');
        return allScoreDates.includes(dateString);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return dayjs(dateString).format('MMM DD, YYYY');
    };

    // Calculate final score percentage - recalculates when scores or goals change
    const finalScorePercentage = useMemo(() => {
        if (!userKpiWithGoals?.goals || userKpiWithGoals.goals.length === 0 || !scores || scores.length === 0) {
            return null;
        }

        let totalWeightedScore = 0;
        let totalWeight = 0;
        let goalsWithScores = 0;

        userKpiWithGoals.goals.forEach(goalItem => {
            const weight = Number(goalItem.weight) || 0;
            
            // Find scores for this goal from Redux scores array
            const goalScores = scores.filter(score => score.userKpiGoalId === goalItem.id);
            
            if (goalScores.length > 0) {
                goalsWithScores++;
                totalWeight += weight;
                
                const totalScore = goalScores.reduce((sum, s) => sum + Number(s.score), 0);
                const averageScore = totalScore / goalScores.length;
                const weightedScore = (averageScore / 5) * weight;
                totalWeightedScore += weightedScore;
            }
        });

        if (goalsWithScores === 0 || totalWeight === 0) {
            return null;
        }

        const percentageScore = (totalWeightedScore / totalWeight) * 100;
        return percentageScore % 1 === 0 ? percentageScore.toString() : percentageScore.toFixed(1);
    }, [userKpiWithGoals, scores]);

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Dialog 
                    open={open} 
                    onClose={handleClose}
                    maxWidth="xl"
                    fullWidth
                    disableRestoreFocus
                    PaperProps={{
                        sx: { maxHeight: '85vh' }
                    }}
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h6" component="div">
                                    Score Management
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {employeeName || 'Loading...'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ({userKpi?.userId})
                                    </Typography>
                                </Stack>
                                {employeeDesignation && (
                                    <Chip label={employeeDesignation} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }} />
                                )}
                            </Box>
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent dividers>
                        {dialogLoading || !userKpiWithGoals || !userKpiWithGoals?.period ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Add New Score Section */}
                                {addingNewScore && (
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1" fontWeight="600">
                                                Add New Scores
                                            </Typography>
                                            <DatePicker
                                                label="Score Date"
                                                value={scoreDate}
                                                onChange={(newValue) => setScoreDate(newValue)}
                                                minDate={periodStartDate}
                                                maxDate={maxAllowedDate}
                                                shouldDisableDate={shouldDisableDate}
                                                renderInput={(params) => (
                                                    <TextField 
                                                        {...params} 
                                                        size="small"
                                                        sx={{ width: 200 }}
                                                        helperText={
                                                            shouldDisableDate(scoreDate) 
                                                                ? "This date already has scores recorded" 
                                                                : "Select a date within the KPI period"
                                                        }
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                                            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600, width: '35%' }}>Goal</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, width: 90 }}>Weight</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, width: 110 }}>Score (0-5)</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, width: 'auto' }}>Remarks</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {goals.map((goal) => (
                                                        <TableRow key={goal.id}>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight="500" noWrap>
                                                                    {goal.goal?.name || 'Unnamed Goal'}
                                                                </Typography>
                                                                {goal.goal?.description ? (
                                                                    <Tooltip title={goal.goal.description} arrow>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            color="text.secondary"
                                                                            sx={{ 
                                                                                display: 'block',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap',
                                                                                maxWidth: '100%',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        >
                                                                            {goal.goal.description}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                ) : null}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={`${goal.weight}%`} 
                                                                    size="small" 
                                                                    color="primary" 
                                                                    variant="outlined" 
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    size="small"
                                                                    fullWidth
                                                                    value={newScores[goal.id]?.score || ''}
                                                                    onChange={(e) => handleScoreChange(goal.id, e.target.value)}
                                                                    onBlur={() => handleScoreBlur(goal.id)}
                                                                    inputProps={{ min: 0, max: 5, step: 1 }}
                                                                    placeholder="0-5"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    multiline
                                                                    rows={1}
                                                                    value={newScores[goal.id]?.remarks || ''}
                                                                    onChange={(e) => handleRemarksChange(goal.id, e.target.value)}
                                                                    label={isScoreRemarksRequired(newScores[goal.id]?.score) ? 'Remarks (minimum 50 characters)' : 'Remarks'}
                                                                    placeholder={isScoreRemarksRequired(newScores[goal.id]?.score) ? 'Minimum 50 characters required for this score' : 'Optional remarks'}
                                                                    error={!!remarksErrors[goal.id]}
                                                                    helperText={remarksErrors[goal.id] || ''}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                            <Button onClick={handleCancelAdd} disabled={dialogLoading}>
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={dialogLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                                                onClick={handleSaveScores}
                                                disabled={dialogLoading}
                                            >
                                                {dialogLoading ? 'Saving...' : 'Save Scores'}
                                            </Button>
                                        </Box>
                                    </Paper>
                                )}

                                {/* Scores History Table */}
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6">
                                            Score History {finalScorePercentage && (
                                                <Typography component="span" sx={{ 
                                                    color: finalScorePercentage >= 80 ? 'success.main' : finalScorePercentage >= 50 ? 'warning.main' : 'error.main',
                                                    fontWeight: 'bold'
                                                }}>
                                                    ({finalScorePercentage}%)
                                                </Typography>
                                            )}
                                        </Typography>
                                        {!addingNewScore && (
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddScoreClick}
                                                disabled={goals.length === 0}
                                            >
                                                Add Scores
                                            </Button>
                                        )}
                                    </Box>
                                    
                                    {goals.length === 0 ? (
                                        <Alert severity="warning">
                                            No goals assigned to this User KPI. Please add goals first.
                                        </Alert>
                                    ) : scores.length === 0 ? (
                                        <Alert severity="info">
                                            No scores recorded yet. Click "Add Scores" to start recording.
                                        </Alert>
                                    ) : (
                                        <TableContainer component={Paper} sx={{ maxHeight: 500, overflowX: 'auto' }}>
                                            <Table stickyHeader size="small" sx={{ minWidth: 'max-content' }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell 
                                                            sx={{ 
                                                                fontWeight: 600, 
                                                                width: 360,
                                                                minWidth: 360,
                                                                maxWidth: 360,
                                                                position: 'sticky',
                                                                left: 0,
                                                                zIndex: 3,
                                                                bgcolor: 'background.paper'
                                                            }}
                                                        >
                                                            Goal
                                                        </TableCell>
                                                        <TableCell 
                                                            sx={{ 
                                                                fontWeight: 600, 
                                                                width: 100,
                                                                minWidth: 100,
                                                                maxWidth: 100,
                                                                position: 'sticky',
                                                                left: 360,
                                                                zIndex: 3,
                                                                bgcolor: 'background.paper'
                                                            }}
                                                        >
                                                            Weight
                                                        </TableCell>
                                                        <TableCell 
                                                            sx={{ 
                                                                fontWeight: 600, 
                                                                width: 150,
                                                                minWidth: 150,
                                                                maxWidth: 150,
                                                                position: 'sticky',
                                                                left: 460,
                                                                zIndex: 3,
                                                                textAlign: 'center',
                                                                bgcolor: 'background.paper',
                                                                borderRight: '1px solid',
                                                                borderRightColor: 'divider'
                                                            }}
                                                        >
                                                            Final Score
                                                        </TableCell>
                                                        {allScoreDates.map((date, idx) => (
                                                            <TableCell 
                                                                key={idx} 
                                                                sx={{ 
                                                                    fontWeight: 600, 
                                                                    width: 150,
                                                                    minWidth: 150,
                                                                    maxWidth: 150,
                                                                    textAlign: 'center'
                                                                }}
                                                            >
                                                                {formatDate(date)}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {goals.map((goal) => (
                                                        <TableRow 
                                                            key={goal.id} 
                                                            hover
                                                        >
                                                            <TableCell 
                                                                className="sticky-cell"
                                                                sx={{ 
                                                                    position: 'sticky',
                                                                    left: 0,
                                                                    zIndex: 2,
                                                                    bgcolor: 'background.paper',
                                                                    width: 360,
                                                                    minWidth: 360,
                                                                    maxWidth: 360
                                                                }}
                                                            >
                                                                <Typography variant="body2" fontWeight="500" noWrap>
                                                                    {goal.goal?.name || 'Unnamed Goal'}
                                                                </Typography>
                                                                {goal.goal?.description ? (
                                                                    <Tooltip title={goal.goal.description} arrow>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            color="text.secondary"
                                                                            sx={{ 
                                                                                display: 'block',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap',
                                                                                maxWidth: '100%',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        >
                                                                            {goal.goal.description}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                ) : null}
                                                            </TableCell>
                                                            <TableCell 
                                                                className="sticky-cell"
                                                                sx={{ 
                                                                    position: 'sticky',
                                                                    left: 360,
                                                                    zIndex: 2,
                                                                    bgcolor: 'background.paper',
                                                                    width: 100,
                                                                    minWidth: 100,
                                                                    maxWidth: 100
                                                                }}
                                                            >
                                                                <Chip 
                                                                    label={`${goal.weight}%`} 
                                                                    size="small" 
                                                                    color="primary" 
                                                                    variant="outlined" 
                                                                />
                                                            </TableCell>
                                                            <TableCell 
                                                                className="sticky-cell"
                                                                sx={{ 
                                                                    position: 'sticky',
                                                                    left: 460,
                                                                    zIndex: 2,
                                                                    bgcolor: 'background.paper',
                                                                    width: 150,
                                                                    minWidth: 150,
                                                                    maxWidth: 150,
                                                                    textAlign: 'center',
                                                                    borderRight: '1px solid',
                                                                    borderRightColor: 'divider'
                                                                }}
                                                            >
                                                                {(() => {
                                                                    const goalScores = groupedScores[goal.id] || [];
                                                                    if (goalScores.length === 0) {
                                                                        return (
                                                                            <Typography variant="body2" color="text.disabled">
                                                                                -
                                                                            </Typography>
                                                                        );
                                                                    }
                                                                    
                                                                    // Calculate average score
                                                                    const totalScore = goalScores.reduce((sum, s) => sum + Number(s.score), 0);
                                                                    const averageScore = totalScore / goalScores.length;
                                                                    
                                                                    // Convert to weighted score: (averageScore / 5) × weight
                                                                    const weightedScore = (averageScore / 5) * goal.weight;
                                                                    
                                                                    // Format numbers: remove .0 for whole numbers
                                                                    const formatScore = (num) => {
                                                                        return num % 1 === 0 ? num.toString() : num.toFixed(1);
                                                                    };
                                                                    
                                                                    const formatWeighted = (num) => {
                                                                        return num % 1 === 0 ? num.toString() : num.toFixed(2);
                                                                    };
                                                                    
                                                                    const label = `${formatScore(averageScore)}/5`;
                                                                    
                                                                    return (
                                                                        <Chip
                                                                            label={label}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color={
                                                                                averageScore >= 4 ? 'success' :
                                                                                averageScore >= 2 ? 'warning' :
                                                                                'error'
                                                                            }
                                                                        />
                                                                    );
                                                                })()}
                                                            </TableCell>
                                                            {allScoreDates.map((date, idx) => {
                                                                const scoreForDate = (groupedScores[goal.id] || [])
                                                                    .find(s => s.scoreDate === date);
                                                                
                                                                const formatScore = (score) => {
                                                                    const numScore = Number(score);
                                                                    const isWholeNumber = numScore % 1 === 0;
                                                                    return isWholeNumber ? `${numScore}/5` : `${numScore.toFixed(1)}/5`;
                                                                };

                                                                const cellKey = `${goal.id}-${date}`;
                                                                const isHovered = hoveredCell === cellKey;
                                                                
                                                                return (
                                                                    <TableCell 
                                                                        key={idx}
                                                                        sx={{ 
                                                                            textAlign: 'center',
                                                                            width: 150,
                                                                            minWidth: 150,
                                                                            maxWidth: 150,
                                                                            position: 'relative'
                                                                        }}
                                                                        onMouseEnter={() => setHoveredCell(cellKey)}
                                                                        onMouseLeave={() => setHoveredCell(null)}
                                                                    >
                                                                        {scoreForDate ? (
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                                                <Chip
                                                                                    label={formatScore(scoreForDate.score)}
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    color={
                                                                                        Number(scoreForDate.score) >= 4 ? 'success' :
                                                                                        Number(scoreForDate.score) >= 2 ? 'warning' :
                                                                                        'error'
                                                                                    }
                                                                                    sx={{ cursor: 'pointer' }}
                                                                                />
                                                                                {scoreForDate.remarks && (
                                                                                    <Tooltip title={scoreForDate.remarks} arrow>
                                                                                        <IconButton 
                                                                                            size="small"
                                                                                            onClick={() => handleShowRemark(scoreForDate.remarks)}
                                                                                        >
                                                                                            <InfoIcon fontSize="small" color="info" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                )}
                                                                                <Box 
                                                                                    sx={{ 
                                                                                        display: 'flex', 
                                                                                        alignItems: 'center', 
                                                                                        justifyContent: 'center',
                                                                                        gap: 0.5,
                                                                                        bgcolor: (theme) => alpha(theme.palette.divider, 0.06),
                                                                                        borderRadius: 1,
                                                                                        p: 0.2,
                                                                                        visibility: isHovered ? 'visible' : 'hidden'
                                                                                    }}
                                                                                >
                                                                                    <Tooltip title="Edit Score">
                                                                                        <IconButton 
                                                                                            size="small"
                                                                                            color="primary"
                                                                                            onClick={() => handleEditScore(scoreForDate, goal.id, date)}
                                                                                        >
                                                                                            <EditIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                    <Tooltip title="Delete Score">
                                                                                        <IconButton 
                                                                                            size="small"
                                                                                            color="error"
                                                                                            onClick={() => handleDeleteClick(scoreForDate.id)}
                                                                                        >
                                                                                            <DeleteIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </Box>
                                                                            </Box>
                                                                        ) : (
                                                                            <Box>
                                                                                {isHovered ? (
                                                                                    <Box 
                                                                                        sx={{ 
                                                                                            display: 'flex', 
                                                                                            alignItems: 'center', 
                                                                                            justifyContent: 'center',
                                                                                            width: '100%',
                                                                                            height: '100%',
                                                                                            minHeight: 32,
                                                                                            bgcolor: (theme) => alpha(theme.palette.divider, 0.06),
                                                                                            borderRadius: 1,
                                                                                            p: 0.5
                                                                                        }}
                                                                                    >
                                                                                        <Tooltip title="Add Score">
                                                                                            <IconButton 
                                                                                                size="small"
                                                                                                color="success"
                                                                                                onClick={() => handleAddSingleScore(goal.id, date)}
                                                                                            >
                                                                                                <AddIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Typography variant="body2" color="text.disabled">
                                                                                        -
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    ))}
                                                    
                                                    {/* Add Goal Row */}
                                                    <TableRow hover>
                                                        <TableCell 
                                                            className="sticky-cell"
                                                            sx={{ 
                                                                width: 360,
                                                                minWidth: 360,
                                                                maxWidth: 360,
                                                                position: 'sticky',
                                                                left: 0,
                                                                zIndex: 2,
                                                                bgcolor: 'background.paper'
                                                            }}
                                                        >
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<AddIcon />}
                                                                onClick={handleAddGoal}
                                                                size="small"
                                                                fullWidth
                                                                sx={{ my: 1, py: 1, maxWidth: '200px' }}
                                                            >
                                                                Add a New Goal
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell 
                                                            className="sticky-cell"
                                                            sx={{ 
                                                                width: 100,
                                                                minWidth: 100,
                                                                maxWidth: 100,
                                                                position: 'sticky',
                                                                left: 360,
                                                                zIndex: 2,
                                                                bgcolor: 'background.paper'
                                                            }}
                                                        >
                                                        </TableCell>
                                                        <TableCell 
                                                            className="sticky-cell"
                                                            sx={{ 
                                                                width: 150,
                                                                minWidth: 150,
                                                                maxWidth: 150,
                                                                position: 'sticky',
                                                                left: 460,
                                                                zIndex: 2,
                                                                bgcolor: 'background.paper',
                                                                borderRight: '1px solid',
                                                                borderRightColor: 'divider'
                                                            }}
                                                        >
                                                        </TableCell>
                                                        {allScoreDates.map((date, idx) => (
                                                            <TableCell 
                                                                key={idx}
                                                                sx={{ 
                                                                    width: 150,
                                                                    minWidth: 150,
                                                                    maxWidth: 150
                                                                }}
                                                            >
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Score Calculation Summary */}
                        <Box sx={(theme) => ({ mt: 3, p: 2, bgcolor: theme.palette.background.paper, borderRadius: 1, border: '1px solid', borderColor: theme.palette.divider })}>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                Score Calculation Summary
                            </Typography>
                            
                            {(() => {
                                if (!userKpiWithGoals?.goals || userKpiWithGoals.goals.length === 0) {
                                    return (
                                        <Typography variant="body2" color="text.secondary">
                                            No goals available for calculation
                                        </Typography>
                                    );
                                }

                                let totalWeightedScore = 0;
                                let totalWeight = 0;
                                let goalsWithScores = 0;
                                const goalCalculations = [];

                                userKpiWithGoals.goals.forEach(goalItem => {
                                    const weight = Number(goalItem.weight) || 0;
                                    
                                    // Find scores for this goal from Redux scores array
                                    const goalScores = scores.filter(score => score.userKpiGoalId === goalItem.id);
                                    
                                    if (goalScores.length > 0) {
                                        goalsWithScores++;
                                        totalWeight += weight;
                                        
                                        // Calculate average score for this goal
                                        const totalScore = goalScores.reduce((sum, s) => sum + Number(s.score), 0);
                                        const averageScore = totalScore / goalScores.length;
                                        
                                        // Calculate weighted score: (averageScore / 5) × weight
const weightedScore = (averageScore / 5) * weight;
                                        totalWeightedScore += weightedScore;
                                        
                                        goalCalculations.push({
                                            goalName: goalItem.goal?.name || 'Unnamed Goal',
                                            weight,
                                            averageScore,
                                            weightedScore,
                                            scoreCount: goalScores.length
                                        });
                                    }
                                });

                                if (goalsWithScores === 0 || totalWeight === 0) {
                                    return (
                                        <Typography variant="body2" color="text.secondary">
                                            No scores available for calculation
                                        </Typography>
                                    );
                                }

                                // Calculate final results
                                const percentageScore = (totalWeightedScore / totalWeight) * 100;
                                const overallAverageScore = percentageScore / 20;
                                
                                // Format numbers
                                const formatScore = (num) => {
                                    return num % 1 === 0 ? num.toString() : num.toFixed(1);
                                };

                                return (
                                    <Box>
                                        {/* Individual Goal Calculations */}
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                                            Goal Breakdown:
                                        </Typography>
                                        {goalCalculations.map((calc, index) => (
                                            <Box key={index} sx={{ mb: 1, pl: 1, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                                                <Typography variant="body2">
                                                    <strong>{calc.goalName}</strong> ({calc.scoreCount} score{calc.scoreCount !== 1 ? 's' : ''})
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Average: {formatScore(calc.averageScore)}/5 × Weight: {calc.weight}% = Weighted: {formatScore(calc.weightedScore)}%
                                                </Typography>
                                            </Box>
                                        ))}
                                        
                                        {/* Summary Calculations */}
                                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="body2">
                                                <strong>Total Weighted Score:</strong> {formatScore(totalWeightedScore)}%
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Total Weight:</strong> {totalWeight}%
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <strong>Overall Score:</strong> {formatScore(percentageScore)}% ({formatScore(overallAverageScore)}/5)
                                            </Typography>
                                        </Box>
                                        
                                        {/* Final Result */}
                                        <Box sx={{
                                            mt: 2,
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            backgroundColor: (theme) => theme.palette.background.paper,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    Final Score
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    Based on {goalsWithScores} goal{goalsWithScores !== 1 ? 's' : ''}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 'bold',
                                                    color: overallAverageScore >= 4 ? 'success.main' : overallAverageScore >= 2 ? 'warning.main' : 'error.main'
                                                }}>
                                                    {formatScore(percentageScore)}%
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {formatScore(overallAverageScore)}/5
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })()}
                        </Box>

                        {/* Marking Criteria */}
                        <Paper elevation={0} sx={(theme) => ({ p: 3, borderRadius: 2, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, mt: 3 })}>
                            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                                Marking Criteria
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Understanding the scoring scale for performance evaluation
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                                Scores are automatically rounded to the nearest whole number (0, 1, 2, 3, 4, or 5) based on your input.
                            </Typography>
                            
                            <TableContainer component={Paper} elevation={0} sx={(theme) => ({ border: `1px solid ${theme.palette.divider}` })}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.divider, 0.06) }}>
                                            <TableCell sx={{ fontWeight: 600, width: 60 }}>Score</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Example</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: 'error.main' }}>0</TableCell>
                                            <TableCell>Had opportunities but no action was taken / Failed</TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Employee was assigned a project deadline extension task but never started working on it despite multiple reminders</TableCell>
                                        </TableRow>
                                        <TableRow sx={(theme) => ({ bgcolor: theme.palette.action.hover })}>
                                            <TableCell sx={{ fontWeight: 600, color: 'warning.dark' }}>1</TableCell>
                                            <TableCell>Started with no outcome / Failing</TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Employee began researching a new process but abandoned it halfway without any deliverables</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: 'warning.main' }}>2</TableCell>
                                            <TableCell>Did not provide feedback / Requires improvement</TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Employee completed the assigned task but didn't communicate progress updates to the team</TableCell>
                                        </TableRow>
                                        <TableRow sx={(theme) => ({ bgcolor: theme.palette.action.hover })}>
                                            <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>3</TableCell>
                                            <TableCell>Actions having little to no effect / Sometimes achieving</TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Employee implemented a small process change that saved 5 minutes per task but didn't scale it</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>4</TableCell>
                                            <TableCell>Able to implement / Achieving</TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Employee successfully automated a manual process, reducing processing time by 30%</TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.divider, 0.03) }}>
                                            <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>5</TableCell>
                                            <TableCell>Achieving above expectation</TableCell>
                                            <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Employee not only automated the process but also trained the team and documented the solution</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            </LocalizationProvider>

            {/* Remarks Dialog */}
            <Dialog
                open={remarksDialog.open}
                onClose={() => setRemarksDialog({ open: false, remark: '' })}
                maxWidth="sm"
                fullWidth
                disableRestoreFocus
            >
                <DialogTitle>Remarks</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {remarksDialog.remark}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemarksDialog({ open: false, remark: '' })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Score Dialog */}
            <Dialog
                open={editDialog.open}
                onClose={() => setEditDialog({ open: false, score: null, goalId: null, date: null })}
                maxWidth="sm"
                fullWidth
                disableRestoreFocus
            >
                <DialogTitle>Edit Score</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Score (0-5)"
                            type="number"
                            fullWidth
                            value={editDialog.score?.score != null ? Number(editDialog.score.score) : ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                const numValue = Number(value);
                                if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 5)) {
                                    return;
                                }
                                setEditDialog(prev => ({
                                    ...prev,
                                    score: { ...prev.score, score: value }
                                }));
                            }}
                            onBlur={() => {
                                setEditDialog(prev => {
                                    const current = prev.score?.score;
                                    if (!current || current === '') return prev;
                                    let numValue = Number(current);
                                    if (isNaN(numValue)) return prev;
                                    numValue = Math.round(numValue);
                                    numValue = Math.max(0, Math.min(5, numValue));
                                    return {
                                        ...prev,
                                        score: { ...prev.score, score: String(numValue) }
                                    };
                                });
                            }}
                            inputProps={{ min: 0, max: 5, step: 1 }}
                        />
                        <TextField
                            label={isScoreRemarksRequired(editDialog.score?.score) ? 'Remarks (minimum 50 characters)' : 'Remarks'}
                            multiline
                            rows={3}
                            fullWidth
                            value={editDialog.score?.remarks || ''}
                            onChange={(e) => setEditDialog(prev => ({
                                ...prev,
                                score: { ...prev.score, remarks: e.target.value }
                            }))}
                            error={editDialog.submitted && isScoreRemarksRequired(editDialog.score?.score) && getRemarksError(editDialog.score?.score, editDialog.score?.remarks) !== ''}
                            helperText={editDialog.submitted && isScoreRemarksRequired(editDialog.score?.score) ? 'Minimum 50 characters required' : 'Optional'}
                            required={isScoreRemarksRequired(editDialog.score?.score)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog({ open: false, score: null, goalId: null, date: null, submitted: false })}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleUpdateScore}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, scoreId: null })}
                maxWidth="xs"
                fullWidth
                disableRestoreFocus
            >
                <DialogTitle>Delete Score</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this score? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, scoreId: null })}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="error"
                        onClick={handleConfirmDelete}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Single Score Dialog */}
            <Dialog
                open={addScoreDialog.open}
                onClose={() => setAddScoreDialog({ open: false, goalId: null, date: null, score: '', remarks: '' })}
                maxWidth="sm"
                fullWidth
                disableRestoreFocus
            >
                <DialogTitle>Add Score</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Score (0-5)"
                            type="number"
                            fullWidth
                            value={addScoreDialog.score || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                const numValue = Number(value);
                                if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 5)) {
                                    return;
                                }
                                setAddScoreDialog(prev => ({ ...prev, score: value }));
                            }}
                            onBlur={() => {
                                setAddScoreDialog(prev => {
                                    const current = prev.score;
                                    if (!current || current === '') return prev;
                                    let numValue = Number(current);
                                    if (isNaN(numValue)) return prev;
                                    numValue = Math.round(numValue);
                                    numValue = Math.max(0, Math.min(5, numValue));
                                    return { ...prev, score: String(numValue) };
                                });
                            }}
                            inputProps={{ min: 0, max: 5, step: 1 }}
                        />
                        <TextField
                            label={isScoreRemarksRequired(addScoreDialog.score) ? 'Remarks (minimum 50 characters)' : 'Remarks'}
                            multiline
                            rows={3}
                            fullWidth
                            value={addScoreDialog.remarks || ''}
                            onChange={(e) => setAddScoreDialog(prev => ({ ...prev, remarks: e.target.value }))}
                            error={addScoreDialog.submitted && isScoreRemarksRequired(addScoreDialog.score) && getRemarksError(addScoreDialog.score, addScoreDialog.remarks) !== ''}
                            helperText={addScoreDialog.submitted && isScoreRemarksRequired(addScoreDialog.score) ? 'Minimum 50 characters required' : 'Optional'}
                            required={isScoreRemarksRequired(addScoreDialog.score)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddScoreDialog({ open: false, goalId: null, date: null, score: '', remarks: '', submitted: false })}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSaveNewScore}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Goal Dialog */}
            <AddEditGoalDialog
                open={addGoalDialogOpen}
                isEditMode={false}
                goal={null}
                userKpiId={userKpi?.id}
                onClose={handleAddGoalDialogClose}
                onSubmit={handleGoalAdded}
            />
        </>
    );
};

export default ScoreManagementDialog;
